<div class="contentInner">
  <h1 class="sectionHeader">Connecting Dash to an Microsoft 365 Enterprise Application</h1>
  <p class="intro">
    Connecting Dash to Microsoft 365 through an Enterprise Application is recommended for most organizations.  The setup procedure  must be
    performed by a global administrator in your Microsoft 365 organization.  If this is not possible, then you can connect
    Dash to a <a routerLink="/guide/o365-service-account">service account</a> instead.
  </p>

  <p>
    Once you have set up your meeting rooms, you will need give Dash access to your Microsoft 365 organization by creating an
    <i>app registration</i>.  A global administrator needs to complete the steps below to create the application,
    grant it the necessary permissions, and share its credentials with you.
  </p>
  <ol class="instructionList">
    <li>
      <div>
        <p><b>Create a new App registration:</b><br/> In the App registration section of the
          <a href="https://portal.azure.com/#blade/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/RegisteredApps" target="_blank">Microsoft Azure Portal</a>.<br/><br/>  Select
          <i>New registration</i>.  Give the registration a useful name (such as 'Dash').  <br/><br/>When asked who can use or access this API, select <i>"Accounts in
          this organizational directory only" </i>and click <i>Register</i>.
            You do not need to enter a redirect URI.</p>
        <app-illustration class="illustration officeCreateAppRegistration" alt="Register a Dash App Registration in the Azure Portal" src="../../assets/images/office-create-app-registration.png" webpSrc="../../assets/images/office-create-app-registration.webp" width="170" height="224"></app-illustration>
      </div>
    </li>
    <li>
      <div>
        <div class="liContainer">
        <p><b>Add the correct permissions:</b><br/> You should now be on the overview page of your new app registration.
          Click on <i>API Permissions</i> in the left-hand navigation menu, and then click <i>Add a permission</i>.</p>

        <p>Click on <i>Microsoft Graph</i> then click on <i>Application permissions</i>, and add the following 4 permissions:</p>
        <ul>
          <li class="permissionLabel">Calendars.readWrite</li>
          <li class="permissionLabel">Organization.Read.All</li>
          <li class="permissionLabel">Place.Read.All</li>
          <li class="permissionLabel">User.Read.All</li>
        </ul>

        </div>

        <app-illustration class="illustration officeSetPermissions" alt="Register the necessary permissions for Dash in your App Registration" src="../../assets/images/office-set-permissions.png" webpSrc="../../assets/images/office-set-permissions.webp" width="170" height="181"></app-illustration>
      </div>
      <p>When you have added the permissions, your table should look like the illustration, with all permissions being of type 'Application'.  If any of them are type 'Delegated' you should remove them and re-add them as 'Application' permissions.</p>
      <p>When you have finished adding all the permissions, click <i>Grant admin consent for [your organization]</i>
        and click <i>Yes</i>.  You can find a detailed explanation of how Dash uses each of these permissions at the end
        of this page.</p>
    </li>
    <li>
      <div>
        <div class="liContainer">
        <p><b>Generate the client-secret:</b><br/> Click on <i>Certificates & secrets</i> in the left-hand navigation
          menu, and then make sure that the <i>Client secrets</i> tab is selected.  Click <i>New client secret</i>.
          Enter a descriptive name (such as "Dash display devices") and choose an expiry time.  Make sure to copy the
          client secret's value, as it will only be available now.</p>


        </div>
        <app-illustration class="illustration officeCreateClientSecret" alt="Create a client secret for your Dash App Registration" src="../../assets/images/office-create-client-secret.png" webpSrc="../../assets/images/office-create-client-secret.webp" width="170" height="204"></app-illustration>
      </div>
      <p class="tip icon">When choosing the expiry
        time, it is important to be aware that you will need to create a new client secret and set it in Dash when the
        one you are creating now expires.</p>
    </li>
    <li>
      <div>
        <p><b>Copy the credentials:</b><br/>Making sure you have noted down the client secret, select the <i>Overview</i>
          page in the left-hand navigation menu.  You should see a number of fields listed in the <i>Essentials</i>
          section.  <br><br>Copy the <i>Application (client) ID</i> and <i>Directory (tenant) ID</i> and give them to the person
          setting up Dash, along with the client secret.</p>
        <app-illustration class="illustration officeGetCredentials" alt="Copy the Tenant ID and Client ID from your Dash App Registration" src="../../assets/images/office-get-credentials.png" webpSrc="../../assets/images/office-get-credentials.webp" width="170" height="244"></app-illustration>
      </div>
    </li>
  </ol>


  <h2>Connecting Dash to the account</h2>
  <p>
    Once you have completed the sharing steps above, you can connect your Microsoft 365 account to Dash.  Open
    Dash and go to Settings (the <i>cog</i> icon in the bottom right of the main display).  If this is your first time using Dash,
    you will be asked to choose a calendar service immediately.  If you have used Dash before you should see the name
    of the calendar service you are currently connected to, and a <i>Change</i> button.  Click <i>Change</i>.
  </p>
  <p>
    You should see a list of calendar services to choose from (Microsoft 365, Google, and your device calendar).  Select
    <b>Microsoft 365 or Microsoft Account</b> and then select "Enterprise Application".  On the next screen, enter the
    credentials you obtained above into the app.  It can be helpful to send the credentials electronically to the device
    first, and then copy+paste them into the fields.
  </p>
  <p>
    Once you have entered the credentials, click <i>Next</i> and Dash will connect to your Microsoft 365 account and will
    give you a list of your organization's meeting rooms.  For more information on getting Dash set up, please see our
    <a routerLink="/guide/quickStart">Quick start guide</a>.
  </p>

  <p>&nbsp;</p>
  <p>&nbsp;</p>

  <p class="tip icon"><b>Dash requires several permissions to function.  Below is a list of each permission, and the reason each is required:</b><br><br>
    <span class="code">Calendars.readWrite</span> - Dash uses this to read your meeting events, create room bookings, and extend or cancel meetings.  It is also used to create the meeting in the organizer's calendar, if you allow your users to set the organizer in meetings booked from the display.<br><br>
    <span class="code">Place.Read.All</span> - This is required in order for Dash to discover your room mailboxes<br><br>
    <span class="code">Organization.Read.All (optional)</span> - Dash displays your organization name on the connection screen to help you identify which account Dash is connected to.  If this permission is not granted, Dash will not show the name of the organization you are connected to.<br><br>
    <span class="code">User.Read.All (optional)</span> - Dash uses this permission to search your user directory when setting the organizer for meetings or adding attendees.  It is also used to read the default timezone for the meeting room calendar, so that booked meetings show up in the correct timezone for your room.  If this permission is not granted, users booking from the display will not be able to set the organizer or add attendees to their meetings.  The timezone of meetings booked will be set to the display device's local timezone.<br><br>
    Dash devices communicate directly with Microsoft 365, so none of your sensitive information is shared with us or our servers.  The credential information is safely stored in your device's secure storage.
    <br/><br/>
    <b>Important:</b> Administrators can configure an <a href="https://learn.microsoft.com/en-us/graph/auth-limit-mailbox-access">application access policy</a> to limit Dash's access to specific mailboxes and not to all the mailboxes in the organization, even if Dash has been granted the application permissions above.
  </p>
</div>
